import { useCallback, useEffect, useMemo, useState } from 'react';
import { ServiceConnectionStatus } from 'fym-shared/src/smartlinks/catalog';
import { useGetServiceConnectionsQuery } from '../../redux/api/fymAPI';

/** How often the page asks whether the phone finished (design 1a note). */
export const PHONE_CONNECT_POLL_MS = 5000;
/** After this the page stops asking and shows design 2c. */
export const PHONE_CONNECT_TIMEOUT_MS = 10 * 60 * 1000;

export type PhoneConnectStatus = 'waiting' | 'connected' | 'timeout';

interface UsePhoneConnectWatchProps {
  accountId: number;
  integrationType: string;
}

/**
 * Watches the service connections while the reader connects a service on the phone. The service
 * counts as connected when its row is connected and carries an artist. Polling stops at the connection or after
 * ten minutes; `checkAgain` asks once more and starts a new ten minutes.
 */
export function usePhoneConnectWatch({ accountId, integrationType }: UsePhoneConnectWatchProps) {
  const [isTimedOut, setIsTimedOut] = useState(false);
  const [round, setRound] = useState(0);

  const { data, refetch } = useGetServiceConnectionsQuery(
    { accountId },
    { pollingInterval: isTimedOut ? 0 : PHONE_CONNECT_POLL_MS }
  );

  const row = useMemo(
    () => data?.connections.find((connection) => connection.integrationType === integrationType),
    [data, integrationType]
  );
  const artist = row?.artist ?? null;
  // A Reconnect keeps the artist on the row, so the status must say `connected` too.
  const isConnected = artist !== null && row?.status === ServiceConnectionStatus.connected;

  useEffect(() => {
    if (isConnected) {
      return undefined;
    }
    const timer = window.setTimeout(() => setIsTimedOut(true), PHONE_CONNECT_TIMEOUT_MS);
    return () => window.clearTimeout(timer);
  }, [isConnected, round]);

  const checkAgain = useCallback(() => {
    setIsTimedOut(false);
    setRound((value) => value + 1);
    refetch().catch(console.error);
  }, [refetch]);

  let status: PhoneConnectStatus = 'waiting';
  if (isConnected) {
    status = 'connected';
  } else if (isTimedOut) {
    status = 'timeout';
  }

  return { status, artistName: artist?.name, checkAgain };
}
